import { createPortal } from "react-dom"
import { TintViewer } from "components/TintViewer"
import { useTranslation } from "react-i18next"
import { type ProductV2 } from "types"
import { Button } from "@/components/ui/button"
import { PreviewOverlayImageSelector } from "./PreviewOverlayImageSelector"
import { ProductPreview } from "./ProductPreview"
import { MODEL_IMAGES } from "./types"
import { useEffect } from "react"
import { ProductCategories } from "components/Navbar/types"
import { X } from "lucide-react"

const TINT_VIEWER_SIZE = 520

interface PreviewOverlayProps {
  productItem: ProductV2
  image: string
  onChangeImage: (url: string) => void
  onClose: () => void
  setActiveColorIndex?: (color: number) => void
  effectType?: ProductCategories
  activeColorIndex?: number
  glassesTransformMode?: "raw" | "controlled"
}

export const PreviewOverlay = ({
  onClose,
  onChangeImage,
  image,
  productItem,
  setActiveColorIndex,
  effectType = ProductCategories.MAKEUP,
  activeColorIndex = 0,
  glassesTransformMode = "raw",
}: PreviewOverlayProps) => {
  const { t } = useTranslation()

  // Lock body scroll when overlay is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [])

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleEsc)
    return () => window.removeEventListener("keydown", handleEsc)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col" role="dialog" aria-modal="true">
      {/* Backdrop — frosted glass */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-xl" onClick={onClose} />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute right-5 top-5 z-20 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white/80 backdrop-blur-sm transition-all hover:bg-white/20 hover:text-white"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Viewer area */}
      <div className="relative flex flex-1 items-center justify-center p-8">
        <div
          className="relative overflow-hidden rounded-2xl shadow-2xl shadow-black/30"
          style={{ width: TINT_VIEWER_SIZE, height: TINT_VIEWER_SIZE }}
        >
          <TintViewer
            effectType={effectType}
            glassesTransformMode={glassesTransformMode}
            productItem={productItem}
            activeColor={
              effectType === ProductCategories.MAKEUP
                ? productItem.colors[activeColorIndex].color
                : ""
            }
            image={image || MODEL_IMAGES[0]}
            size={TINT_VIEWER_SIZE}
          />
        </div>
      </div>

      {/* Bottom control bar — glass morphism */}
      <div className="relative z-10 mx-auto mb-6 w-full max-w-3xl px-4">
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 shadow-lg backdrop-blur-xl">
          {/* Product info */}
          <ProductPreview
            effectType={effectType}
            data={productItem}
            activeColorIndex={activeColorIndex}
            setActiveColorIndex={setActiveColorIndex}
          />

          {/* Model selector */}
          <div className="flex items-center gap-2">
            <PreviewOverlayImageSelector image={image} onChange={onChangeImage} />
          </div>

          {/* Close */}
          <Button
            variant="secondary"
            size="sm"
            className="bg-white/15 text-white hover:bg-white/25 border-0"
            onClick={onClose}
          >
            {t("close")}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
